import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Card, notification, Flex, Space, Popconfirm, message } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, ArrowLeftOutlined, PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { convertKeyToCamelCase, beautifyKey, formatDeleteErrorDetail } from '../../utils/functions';
import LookupForm from './common/LookupForm';
import EditForm from './common/EditForm';
import CreateForm from './common/CreateForm';
import ViewForm from './common/ViewForm';
import DynamicFormChildSP from './common/DynamicFormChildSP';
import ApiErrorAlert from '../common/ApiErrorAlert';
import debounce from 'lodash/debounce';

// use translation hook
import { useTranslation } from 'react-i18next';

// Fields hidden regardless of caller-supplied fieldsToHide. Safe across entities since
// no DTO other than Patient's uses this name.
const DEFAULT_FIELDS_TO_HIDE = ['UploadBatchId'];

const DynamicFormSP = ({
    // selection
    currentEntityId,
    selectedItemIds = [],
    selectedItems,

    listingMode = 'selectedIds',
    // edit mode
    defaultEditMode,
    // search props
    primarySearchProp,
    secondarySearchProp,
    groupSearchProp,
    // entity props
    singularEntity,
    pluralEntity,
    constantPrefix,
    summaryProps,
    loadDto,
    createEntity,
    updateEntity,
    deleteEntity,
    getEntityDetails,
    listEntities,
    listEntitiesByIds,
    //actions and callbacks
    onSelectedEntitiesChange = () => { },
    onSelectedItems = () => { },
    onUpdatedEntityChange = () => { },
    onNewEntityChange = () => { },
    /** Fires once after a successful create, with the created entity payload — not on every details hydrate. */
    onCreatedEntityChange = () => { },
    onEditModeChange = () => { },
    onLoading = () => { },
    // parent props
    isParent = true,
    fieldsToHide = [],

    // Optional props
    enableSearchBar = true,
    enableCreate = true,
    enableActionButtons = true,
    enableExportButtons = false,
    /**
     * When true, opening a record (from the list or via id) lands on a read-only View
     * first; Edit/Create/Delete stay one click away. Set to false to preserve the
     * legacy Edit-first behaviour.
     */
    enableViewMode = true,

}) => {

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const getEntityLabel = (entityKey) => t(entityKey);

    const allFieldsToHide = [...new Set([...DEFAULT_FIELDS_TO_HIDE, ...fieldsToHide])];

    // Reset Redux state on component mount and unmount. Clear DETAILS when opening Create so a
    // previously viewed record in patientDetails (etc.) cannot hydrate into this form / callbacks.
    useEffect(() => {
        dispatch({ type: `${constantPrefix}_CREATE_RESET` });
        dispatch({ type: `${constantPrefix}_UPDATE_RESET` });
        dispatch({ type: `${constantPrefix}_LIST_RESET` });
        dispatch({ type: `${constantPrefix}_LIST_BY_IDS_RESET` });
        if (!currentEntityId) {
            dispatch({ type: `${constantPrefix}_DETAILS_RESET` });
        }
    }, [dispatch, constantPrefix, currentEntityId]);

    // Redux state variables (default {} — some entities omit e.g. ListByIds in store)
    const entityCreate = useSelector((state) => state[`${singularEntity}Create`] || {});
    const { loading: loadingCreate, error: errorCreate, success: successCreate, [singularEntity]: createdEntity } = entityCreate;

    const entityUpdate = useSelector((state) => state[`${singularEntity}Update`] || {});
    const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = entityUpdate;

    const entityList = useSelector((state) => state[`${singularEntity}List`] || {});
    const { loading: loadingList, error: errorList, [pluralEntity]: entities, currentPage, totalPages } = entityList;

    const entityListByIds = useSelector((state) => state[`${singularEntity}ListByIds`] || {});
    const { loading: loadingListByIds, error: errorListByIds, [pluralEntity]: entitiesByIds } = entityListByIds;

    const entityDetails = useSelector((state) => state[`${singularEntity}Details`] || {});
    const { loading: loadingDetails, error: errorDetails, [singularEntity]: entity } = entityDetails;

    const entityDto = useSelector((state) => state[`${singularEntity}Dto`] || {});
    const { [`${singularEntity}Dto`]: entityDtoConfig, loading: loadingDto, error: errorDto } = entityDto;

    const formLoadError = errorDto || errorListByIds || errorList;

    // Translate a "logical" caller-supplied mode into the active mode considering enableViewMode.
    // Opening an existing record (Edit-with-id intent) lands on View first.
    const resolveDefaultMode = (mode) => (enableViewMode && mode === 'Edit' ? 'View' : mode);

    // State variables
    const [entityId, setEntityId] = useState(currentEntityId);
    const [editForm] = Form.useForm();
    const [newForm] = Form.useForm();

    const [editMode, setEditMode] = useState(resolveDefaultMode(defaultEditMode));
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [selectedSubEntityIds, setSelectedSubEntities] = useState([]);
    const [limit, setLimit] = useState(10);
    const [entityListFilter, setEntityListFilter] = useState({ query: '', page: currentPage, limit: limit, filter: primarySearchProp });
    const [isChildFormOpen, setIsChildFormOpen] = useState(false);
    const [selectedEntityConfig, setSelectedEntityConfig] = useState({});
    const [newEntity, setNewEntity] = useState({});
    const [updatedEntity, setUpdatedEntity] = useState({});
    const [viewMode, setViewMode] = useState('table'); // cards or table
    const [deleteLoading, setDeleteLoading] = useState(false);

    // State for showing/hiding child form
    const [showChildForm, setShowChildForm] = useState(false);

    // listen to loadings 
    const isLoading = loadingCreate || loadingUpdate || loadingList || loadingListByIds || loadingDetails || loadingDto;


    // Listen to EditMode changes
    useEffect(() => {
        setEditMode(resolveDefaultMode(defaultEditMode));
    }, [defaultEditMode, enableViewMode]);

    // listen to editMode changes
    useEffect(() => {
        onEditModeChange(editMode);
    }, [editMode]);

    // listen to currentEntityId changes
    useEffect(() => {
        setEntityId(currentEntityId);
    }, [currentEntityId]);

    // listen to setUpdatedEntity 
    useEffect(() => {
        setUpdatedEntity(updatedEntity);
    }, [updatedEntity]);

    // Load DTO and entities on component mount
    useEffect(() => {
        dispatch(loadDto());
        if (editMode === 'Lookup') {
            dispatch(listEntities(entityListFilter));
        }
        if (selectedItemIds && selectedItemIds.length > 0) {
            if (Array.isArray(selectedItemIds)) {
                dispatch(listEntitiesByIds(selectedItemIds));
            } else if (typeof selectedItemIds === 'string' || typeof selectedItemIds === 'number') {
                dispatch(listEntitiesByIds([selectedItemIds]));
            } else {
                dispatch(listEntitiesByIds([]));
            }
        } else {
            dispatch(listEntitiesByIds([]));
        }
    }, [dispatch]);

    // listen to entities changes
    useEffect(() => {
        if (entities && listingMode === 'allEntities') {
            setSelectedEntities(entities);
        }
    }, [entities]);

    // Update selectedEntities when entitiesByIds changes
    useEffect(() => {
        if (entitiesByIds && listingMode === 'selectedIds') {
            setSelectedEntities(entitiesByIds);
        }
    }, [entitiesByIds]);

    // listen to entityListFilter changes
    useEffect(() => {
        dispatch(listEntities(entityListFilter));
    }, [entityListFilter]);


    // Initialize newEntity and set form values when entityDtoConfig changes
    useEffect(() => {
        if (!entityDtoConfig) return;
        var emptyValues = {};
        Object.entries(entityDtoConfig).forEach(([fieldName, fieldProps]) => {
            emptyValues[convertKeyToCamelCase(fieldName)] = fieldProps.initialValue;
        });
        setNewEntity(emptyValues);
        newForm.setFieldsValue(emptyValues);
    }, [entityDtoConfig]);

    // Set form values when updatedEntity changes
    useEffect(() => {
        editForm.setFieldsValue({ ...updatedEntity });
        editForm.validateFields();
        onUpdatedEntityChange(updatedEntity);
    }, [updatedEntity]);

    // Set form values when newEntity changes
    useEffect(() => {
        newForm.setFieldsValue({ ...newEntity });
        newForm.validateFields();
        onNewEntityChange(newEntity);
    }, [newEntity]);


    // Update parent component with selectedEntities
    useEffect(() => {
        onSelectedItems(selectedEntities);
        onSelectedEntitiesChange(selectedEntities);
    }, [selectedEntities]);

    // Update selectedSubEntityIds when entity changes
    useEffect(() => {
        if (entity) {
            setUpdatedEntity(entity);
            // Reset selectedSubEntityIds when switching to a different entity
            setSelectedSubEntities([]);
        }
    }, [entity]);

    // Show success notification on successUpdate; drop back to View so the user
    // sees the saved record and can re-enter Edit on intent.
    useEffect(() => {
        if (successUpdate) {
            openNotification('bottom', t('Success'), t('Updated Successfully'));
            dispatch({ type: `${constantPrefix}_UPDATE_RESET` });
            if (entityId && getEntityDetails) {
                dispatch(getEntityDetails(entityId));
            }
            if (enableViewMode && entityId && editMode === 'Edit') {
                setEditMode('View');
            }
        }
    }, [successUpdate]);

    // Show error notification on errorUpdate
    useEffect(() => {
        if (errorUpdate) {
            openNotification('bottom', t('Error'), t('Update Failed Please refer to the error message at the top of the form'));
            dispatch({ type: `${constantPrefix}_UPDATE_RESET` });
        }
    }, [errorUpdate]);

    // Show success notification on successCreate and switch to edit/view mode
    useEffect(() => {
        if (successCreate && createdEntity) {
            openNotification('bottom', t('Success'), t('Created Successfully'));
            onCreatedEntityChange(createdEntity);
            setEntityId(createdEntity.id);
            setEditMode(enableViewMode ? 'View' : 'Edit');
            dispatch({ type: `${constantPrefix}_CREATE_RESET` });
        }
    }, [successCreate, createdEntity]);

    // Show error notification on errorCreate
    useEffect(() => {
        if (errorCreate) {
            openNotification('bottom', t('Error'), t('Creation Failed Please refer to the error message at the top of the form'));
        }
    }, [errorCreate]);


    // Get entity details when entityId changes
    useEffect(() => {
        if (entityId) {
            dispatch(getEntityDetails(entityId));
        }
    }, [entityId]);

    useEffect(() => {
        onLoading(isLoading);
    }, [loadingCreate, loadingUpdate, loadingList, loadingListByIds, loadingDetails, loadingDto]);

    // Belt-and-braces required-field check on top of AntD's Form.Item rules — protects against
    // any Field-registration edge case (e.g. an untouched field never firing its wrapped
    // onChange) that would otherwise let onFinish fire with a blank mandatory value.
    const findMissingRequiredFields = (payload) =>
        Object.entries(entityDtoConfig || {})
            .filter(([fieldName, fieldProps]) => fieldProps.isRequired && !allFieldsToHide.includes(fieldName))
            .filter(([fieldName]) => {
                const val = payload[convertKeyToCamelCase(fieldName)];
                return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
            })
            .map(([fieldName]) => fieldName);

    // Handle form submission for newEntity — merge Ant Form values so onBlur-only fields
    // are not lost when submit races ahead of the blur setState.
    const handleSubmitNewEntity = () => {
        const fromForm = newForm.getFieldsValue(true);
        const camelCaseEntity = {};
        Object.entries({ ...newEntity, ...fromForm }).forEach(([key, value]) => {
            camelCaseEntity[convertKeyToCamelCase(key)] = value;
        });
        const missing = findMissingRequiredFields(camelCaseEntity);
        if (missing.length > 0) {
            message.error(t('Please fill in all required fields'));
            newForm.validateFields().catch(() => { });
            return;
        }
        dispatch(createEntity(camelCaseEntity));
    };

    // Handle form submission for updatedEntity
    const handleSubmitUpdatedEntity = () => {
        const fromForm = editForm.getFieldsValue(true);
        const camelCaseEntity = {};
        Object.entries({ ...updatedEntity, ...fromForm }).forEach(([key, value]) => {
            camelCaseEntity[convertKeyToCamelCase(key)] = value;
        });
        const missing = findMissingRequiredFields(camelCaseEntity);
        if (missing.length > 0) {
            message.error(t('Please fill in all required fields'));
            editForm.validateFields().catch(() => { });
            return;
        }
        dispatch(updateEntity(entityId, camelCaseEntity));
    };

    // Handle input change for newEntity
    const handleNewEntityInputChange = (fieldName, value) => {
        const lowerCaseFieldName = convertKeyToCamelCase(fieldName);
        setNewEntity({ ...newEntity, [lowerCaseFieldName]: value });
    };

    // Handle input change for updatedEntity
    const handleUpdatedEntityInputChange = (fieldName, value) => {
        const lowerCaseFieldName = convertKeyToCamelCase(fieldName);
        setUpdatedEntity({ ...updatedEntity, [lowerCaseFieldName]: value });
    };

    // Handle data updated
    const handleDataUpdated = (updatedRow) => {
        setSelectedEntities((prevData) =>
            prevData?.map((row) => (row.id === updatedRow.id ? updatedRow : row))
        );
    };

    // Show notification
    const [api, contextHolder] = notification.useNotification();
    const openNotification = (placement, type, description) => {
        api.info({
            message: type,
            description: description,
            placement,
            icon: type === t('Success') ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#eb2f96" />,
        });
    };

    // Handle search
    const onSearch = (value) => {
        setEntityListFilter({ ...entityListFilter, query: value });
    };

    // Open a record from the list. With enableViewMode the user lands on a read-only
    // View first; Edit is a one-click action away.
    const handleEdit = (id) => {
        setEditMode(enableViewMode ? 'View' : 'Edit');
        setEntityId(id);
    };

    const switchToViewMode = () => entityId && setEditMode('View');
    const switchToEditMode = () => entityId && setEditMode('Edit');
    const switchToCreateMode = () => {
        setUpdatedEntity(null);
        setEntityId(null);
        setEditMode('Create');
    };

    // Handle delete
    const handleDelete = (id) => {
        const newSelectedEntities = selectedEntities?.filter(selectedEntity => selectedEntity.id !== id);
        setSelectedEntities(newSelectedEntities);
    }

    const confirmDelete = async () => {
        if (!deleteEntity || !updatedEntity?.id) return;
        setDeleteLoading(true);
        try {
            await dispatch(deleteEntity(updatedEntity.id));
            message.success(t('Deleted Successfully'));
            setEditMode('Lookup');
            setEntityId(null);
            setUpdatedEntity(null);
            handleDelete(updatedEntity.id);
            dispatch(listEntities(entityListFilter));
        } catch (error) {
            message.error(formatDeleteErrorDetail(t, error));
        } finally {
            setDeleteLoading(false);
        }
    };

    // handle reload selected entityIds 
    const handleReloadEntities = () => {
        dispatch(loadDto());
        if (selectedItemIds && selectedItemIds.length > 0) {
            if (Array.isArray(selectedItemIds)) {
                dispatch(listEntitiesByIds(selectedItemIds));
            } else if (typeof selectedItemIds === 'string' || typeof selectedItemIds === 'number') {
                dispatch(listEntitiesByIds([selectedItemIds]));
            } else {
                dispatch(listEntitiesByIds([]));
            }
        } else {
            dispatch(listEntitiesByIds([]));
        }
    }


    // Handle browse entity
    const handleBrowseEntity = (config) => {
        setSelectedSubEntities([]); // Reset selectedSubEntityIds before setting new values
        setSelectedEntityConfig(config);
        if (editMode === 'Edit' && updatedEntity) {
            var selectedSubEntityIds = updatedEntity[convertKeyToCamelCase(config.fieldName)] || [];
            setSelectedSubEntities(selectedSubEntityIds);
        }
        if (editMode === 'Create') {
            var selectedSubEntityIds = newEntity[convertKeyToCamelCase(config.fieldName)] || [];
            setSelectedSubEntities(selectedSubEntityIds);
        }
        setShowChildForm(true);
    };

    // Handle back button click
    const handleBackButtonClick = () => {
        setShowChildForm(false);
    };

    return (
        <div>

            {!showChildForm ? (
                <Card
                    loading={loadingDto}
                    title={
                        <div>
                            {editMode !== 'Lookup' && !isParent ? (
                                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setEditMode('Lookup')}>
                                    {t('Back to')} {getEntityLabel(singularEntity)}
                                </Button>
                            ) : (
                                <Flex gap="middle" align="center" justify={isParent ? 'end' : 'space-between'} hidden={enableCreate === false && isParent}>
                                    {!isParent && (
                                        <div>{t('Select')} {getEntityLabel(pluralEntity)} ({selectedEntities.length} {t('selected')})</div>
                                    )}
                                    {editMode === 'Lookup' && (
                                        <Button type="primary" disabled={!enableCreate} icon={<PlusOutlined />} onClick={switchToCreateMode}>
                                            {t("Create")}
                                        </Button>
                                    )}
                                    {editMode === 'View' && updatedEntity?.id && (
                                        <Space size="small">
                                            <Button type="primary" size="small" icon={<EditOutlined />} onClick={switchToEditMode}>
                                                {t('Edit')}
                                            </Button>
                                            {enableCreate && (
                                                <Button type="default" size="small" icon={<PlusOutlined />} onClick={switchToCreateMode}>
                                                    {t('Create')}
                                                </Button>
                                            )}
                                            {deleteEntity && (
                                                <Popconfirm
                                                    title={t('Are you sure you want to delete this')}
                                                    description={t('This action cannot be undone.')}
                                                    onConfirm={confirmDelete}
                                                    okText={t('Delete')}
                                                    cancelText={t('Cancel')}
                                                    placement="bottomRight"
                                                >
                                                    <Button danger type="text" size="small" icon={<DeleteOutlined />} loading={deleteLoading} title={t('Delete')} />
                                                </Popconfirm>
                                            )}
                                        </Space>
                                    )}
                                    {editMode === 'Edit' && (
                                        <Space size="small">
                                            {enableViewMode && entityId && (
                                                <Button type="default" size="small" icon={<EyeOutlined />} onClick={switchToViewMode}>
                                                    {t('View')}
                                                </Button>
                                            )}
                                            {enableCreate && (
                                                <Button type="default" size="small" icon={<PlusOutlined />} onClick={switchToCreateMode}>
                                                    {t('Create')}
                                                </Button>
                                            )}
                                            {deleteEntity && updatedEntity?.id && (
                                                <Popconfirm
                                                    title={t('Are you sure you want to delete this')}
                                                    description={t('This action cannot be undone.')}
                                                    onConfirm={confirmDelete}
                                                    okText={t('Delete')}
                                                    cancelText={t('Cancel')}
                                                    placement="bottomRight"
                                                >
                                                    <Button danger type="text" size="small" icon={<DeleteOutlined />} loading={deleteLoading} title={t('Delete')} />
                                                </Popconfirm>
                                            )}
                                        </Space>
                                    )}
                                    {editMode === 'Create' && (
                                        <div>{t("Create")}</div>
                                    )}
                                </Flex>
                            )}
                        </div>
                    }
                    style={{ width: '100%' }}
                    bordered={true}
                >

                    {contextHolder}
                    <ApiErrorAlert error={formLoadError} />
                    {(entityDtoConfig) && (
                        <React.Fragment>
                            {/* Lookup Form */}
                            <LookupForm
                                key={`lookup-form-${entityId}`}
                                selectedEntities={selectedEntities}
                                editMode={editMode}
                                singularEntity={singularEntity}
                                pluralEntity={pluralEntity}
                                primarySearchProp={primarySearchProp}
                                secondarySearchProp={secondarySearchProp}
                                groupSearchProp={groupSearchProp}
                                entities={entities}
                                viewMode={viewMode}
                                setViewMode={setViewMode}
                                setSelectedEntities={setSelectedEntities}
                                onSearch={onSearch}
                                onFilterChange={
                                    debounce((filter) => {
                                        dispatch(listEntities(filter));
                                    }, 1000)
                                }
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                                handleReloadEntities={handleReloadEntities}
                                summaryProps={summaryProps}
                                loadingList={loadingList}
                                loadingListByIds={loadingListByIds}
                                loadingDetails={loadingDetails}
                                loadingCreate={loadingCreate}
                                loadingUpdate={loadingUpdate || loadingDetails}
                                entityDtoConfig={entityDtoConfig}
                                entityListFilter={entityListFilter}
                                limit={limit}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                dispatch={dispatch}
                                getEntityDetails={getEntityDetails}
                                updateEntity={updateEntity}
                                errorUpdate={errorUpdate}
                                handleDataUpdated={handleDataUpdated}

                                enableSearchBar={enableSearchBar}
                                enableActionButtons={enableActionButtons}
                            />

                            {/* View Form (read-only) */}
                            {(editMode === 'View' && (updatedEntity?.id || errorDetails)) && (
                                <ViewForm
                                    key={`view-form-${entityId}`}
                                    entity={updatedEntity?.id ? updatedEntity : null}
                                    entityDtoConfig={entityDtoConfig}
                                    fieldsToHide={allFieldsToHide}
                                    error={errorDetails}
                                />
                            )}

                            {/* Edit Form */}
                            {updatedEntity && editMode === 'Edit' && (
                                <EditForm
                                    key={`edit-form-${entityId}`}
                                    editMode={editMode}
                                    updatedEntity={updatedEntity}
                                    loadingUpdate={loadingUpdate || loadingDetails}
                                    editForm={editForm}
                                    handleSubmitUpdatedEntity={handleSubmitUpdatedEntity}
                                    errorUpdate={errorUpdate}
                                    singularEntity={singularEntity}
                                    entityDtoConfig={entityDtoConfig}
                                    handleUpdatedEntityInputChange={handleUpdatedEntityInputChange}
                                    handleBrowseEntity={handleBrowseEntity}
                                    fieldsToHide={allFieldsToHide}
                                    enableExportButtons={enableExportButtons}
                                />
                            )}
                            {/* Create Form */}
                            {(newEntity && editMode === 'Create') && (
                                <CreateForm
                                    key={`create-form${entityId}`}
                                    newEntity={newEntity}
                                    newForm={newForm}
                                    loadingCreate={loadingCreate}
                                    handleSubmitNewEntity={handleSubmitNewEntity}
                                    errorCreate={errorCreate}
                                    singularEntity={singularEntity}
                                    entityDtoConfig={entityDtoConfig}
                                    handleNewEntityInputChange={handleNewEntityInputChange}
                                    handleBrowseEntity={handleBrowseEntity}
                                    fieldsToHide={allFieldsToHide}
                                />
                            )}
                        </React.Fragment>
                    )}
                </Card>
            ) : (
                <DynamicFormChildSP
                    key={`child-form-${entityId}`}
                    editMode={editMode} // Edit or Create
                    selectedItems={selectedSubEntityIds}
                    selectedItemIds={selectedSubEntityIds}
                    isChildFormOpen={isChildFormOpen}
                    setIsChildFormOpen={setIsChildFormOpen}
                    onSelectedItems={onSelectedItems}
                    selectedEntityConfig={selectedEntityConfig}
                    handleUpdatedEntityInputChange={handleUpdatedEntityInputChange}
                    handleNewEntityInputChange={handleNewEntityInputChange}
                    onNewEntityChange={onNewEntityChange}
                    onUpdatedEntityChange={onUpdatedEntityChange}
                    onSelectedEntitiesChange={onSelectedEntitiesChange}
                    onEditModeChange={onEditModeChange}
                    handleBackButtonClick={handleBackButtonClick}
                    parentSingleEntity={singularEntity}
                    parentPluralEntity={pluralEntity}
                    fieldsToHide={allFieldsToHide}
                    isParent={false}
                    enableCreate={enableCreate}
                    enableSearchBar={enableSearchBar}

                />

            )}

        </div>
    );
};
export default DynamicFormSP;
